import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';

import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import FilePresent from '@mui/icons-material/FilePresent';

import { useDataAuth } from '../../hooks/useDataAuth';

import { JsonTree } from '../jsonEditorV2/jsontree/JsonTree';
import { HtmlEditor } from '../htmlEditor/HtmlEditor';
import { ArrayTextEditor } from '../arrayTextEditor/ArrayTextEditor';
import { MultiLanguageEditor } from '../others/MultiLanguageEditor';
import { GenericAutocomplete } from '../others/GenericAutocomplete';
import { MultipleSelect } from './MultipleSelect';
import HelpIconPopover from '../others/HelpIconPopover';

import { ensureArray } from '../../utils/validateResponseApi';
import { isNotEmptyObject, safeParseJSON, safeStringifyJSON } from '../../utils/jsonValidator';
import { evaluateCondition } from '../../utils/evaluateCondition';

const DynamicForm = ({
    values,
    scheme,
    errorInputs,
    onChange,
    handleFileChange,
    handleUploadClick,
    setChanges,
    setValuesSelectGeneric = null,
    variables = {}
}) => {
    const [{ userSession }] = useDataAuth();
    const [formData, setFormData] = useState(values);
    const [optionsByField, setOptionsByField] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
        onChange(e);
    };

    useEffect(() => {
        setFormData(values);
    }, [values])

    const handleSubmit = (e) => e.preventDefault();

    const handleChanges = (name, value) => {
        setFormData((prevValueInputs) => ({
            ...prevValueInputs,
            [name]: value
        }));
        setChanges(name, value);
    }

    useEffect(() => {
        const fetchOptions = async () => {
            const selectFields = scheme.filter((field) => field.type === 'select_trigger' && field.trigger);
            const optionsEntries = await Promise.all(
                selectFields.map(async (field) => {
                    const { url, method, bodyData } = field.trigger[0];
                    try {
                        if (isNotEmptyObject(variables)) {
                            let varb = safeParseJSON(bodyData?.variables);
                            for (let key in variables) {
                                varb[key] = variables[key];
                            }
                            bodyData.variables = safeStringifyJSON(varb, 0);
                        }

                        const response = await axios({
                            method,
                            url,
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${userSession.token}`,
                            },
                            data: bodyData,
                        });
                        const options = response.data || [];
                        const val = options[field.id]?.find(o => o.defaultValue)?.code || '';
                        if (val !== '')
                            handleChanges(field.id, val);
                        return [field.id, options];
                    } catch (error) {
                        console.error(`Error fetching options for ${field.id}`, error);
                        return [field.id, []];
                    }
                })
            );
            setOptionsByField(Object.fromEntries(optionsEntries));
        };
        fetchOptions();
    }, [scheme, userSession.token]);

    //#region Parse data Select
    function parseSelectOptions(options) {
        if (!Array.isArray(options)) return [];
        return options.map(option => {
            if (typeof option === 'string')
                return { name: option, code: option };
            else if (typeof option === 'object' && option?.key && option?.value)
                return { name: option?.key, code: option?.value };
            return null;
        }).filter(Boolean);
    }
    //#endregion

    return (
        <form onSubmit={handleSubmit}>
            {
                scheme.filter(field => evaluateCondition(field.visible))
                    .map((field, index) => (
                        <Box
                            key={field.id || index}
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: '100%', mt: 1.5, position: 'relative' }}
                        >
                            <Box sx={{ width: '15px', display: { xs: 'none', sm: 'block' } }} />
                            <FormGroup
                                sx={{
                                    width: '92%',
                                    mr: '5px',
                                }}
                                className="mt-2"
                            >
                                {
                                    field.type === 'switch' ? (
                                        <FormControl
                                            required={field.required}
                                            error={Boolean(errorInputs[field.id]?.isError)}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        id={field.id}
                                                        name={field.id}
                                                        checked={Boolean(formData[field.id])}
                                                        required={field.required}
                                                        onChange={handleChange}
                                                    />
                                                }
                                                label={field.label}
                                            />
                                            {errorInputs[field.id]?.isError && (
                                                <FormHelperText>{errorInputs[field.id]?.message}</FormHelperText>
                                            )}
                                        </FormControl>
                                    ) : field.type === 'select' ? (
                                        <GenericAutocomplete
                                            options={ensureArray(parseSelectOptions(field.values))}
                                            value={formData[field.id] ? formData[field.id] : ''}
                                            label={field.label}
                                            name={field.id}
                                            onChange={handleChange}
                                            error={Boolean(errorInputs[field.id]?.isError)}
                                            helperText={errorInputs[field.id]?.messageError}
                                            required={field?.required}
                                            disableCloseOnSelect={false}
                                        />
                                    ) : field.type === 'date' ? (
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label={field.label}
                                                value={formData[field.id] || null}
                                                inputFormat="yyyy/MM/dd"
                                                onChange={(newValue) => {
                                                    handleChange({
                                                        target: { name: field.id, value: moment(newValue).format('YYYY/MM/DD') }
                                                    });
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        id={field.id}
                                                        name={field.id}
                                                        size="small"
                                                        fullWidth
                                                        required={field.required}
                                                        error={Boolean(errorInputs[field.id]?.isError)}
                                                        helperText={errorInputs[field.id]?.messageError}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    ) : field.type === 'key_value' ? (
                                        <JsonTree
                                            keyParent={field.id}
                                            contentJson={!formData[field.id] ? {} : formData[field.id]}
                                            handleChange={handleChanges}
                                        />
                                    ) : field.type === 'upload' ? (
                                        <FormGroup
                                            style={{ height: '30px', display: 'flex' }}
                                        >
                                            <TextField
                                                id={field.id}
                                                name={field.id}
                                                size="small"
                                                fullWidth
                                                type="text"
                                                label={field.label}
                                                value={formData[field.id] || ''}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ readOnly: true }}
                                                error={Boolean(errorInputs[field.id]?.isError)}
                                                helperText={errorInputs[field.id]?.messageError}
                                                InputProps={{
                                                    endAdornment: (
                                                        <>
                                                            <label htmlFor={`file-upload-${field.id}`}>
                                                                <Tooltip title={'Seleccionar archivo'}>
                                                                    <FilePresent style={{ fontSize: 25, color: 'gray' }} />
                                                                </Tooltip>
                                                            </label>
                                                            {/* {field?.directCharge &&
                                                                <label>
                                                                    <Tooltip title={'Subir archivo'}>
                                                                        <IconButton
                                                                            id={`id_${field.accion}`}
                                                                            size="small"
                                                                            sx={{ p: 0 }}
                                                                            onClick={() => handleUploadClick(field.buket, field.path)}
                                                                        >
                                                                            <FileUpload style={{ fontSize: 25, color: 'blue' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </label>
                                                            } */}
                                                        </>
                                                    ),
                                                }}
                                            />
                                            <input
                                                accept=".pdf, .xml, .eml, .zip, .jpg, .png"
                                                style={{ display: 'none' }}
                                                id={`file-upload-${field.id}`}
                                                type="file"
                                                onChange={(e) => handleFileChange(e, field.id)}
                                            />
                                        </FormGroup>
                                    ) : field.type === 'html' ? (
                                        <HtmlEditor
                                            field={field}
                                            html={formData[field.id] || ''}
                                            handleChange={handleChanges}
                                        />
                                    ) : field.type === 'textArray' ? (
                                        <ArrayTextEditor
                                            field={field}
                                            arrayText={formData[field.id] || []}
                                            handleChange={handleChanges}
                                        />
                                    ) : field.type === 'select_trigger' ? (
                                        <GenericAutocomplete
                                            options={ensureArray(optionsByField[field.id])}
                                            value={formData[field.id] ? formData[field.id] : ''}
                                            label={field.label}
                                            name={field.id}
                                            onChange={setValuesSelectGeneric}
                                            error={Boolean(errorInputs[field.id]?.isError)}
                                            helperText={errorInputs[field.id]?.messageError}
                                            required={field?.required}
                                            disableCloseOnSelect={false}
                                        />
                                    ) : field.type === 'MLEditor' ? (
                                        <MultiLanguageEditor
                                            value={formData[field.id] || ''}
                                            onChange={(e) => handleChanges(field.id, e)}
                                            dfLanguage={field?.hasOwnProperty('subtype') ? field.subtype : 'plaintext'}
                                            htCtrl={field?.hasOwnProperty('height') ? field.height : '250'}
                                            errorInputs={errorInputs}
                                            field={field}
                                        />
                                    ) : field.type === 'multiple_select' ? (
                                        <MultipleSelect
                                            config={field}
                                            variables={variables}
                                            values={ensureArray(formData[field.id])}
                                            handleChange={(e) => handleChanges(field.id, e.target.value)}
                                            name={field.id}
                                            required={field?.required}
                                            error={Boolean(errorInputs[field.id]?.isError)}
                                            errorText={errorInputs[field.id]?.messageError}
                                        />
                                    ) : (
                                        <TextField
                                            id={field.id}
                                            name={field.id}
                                            size="small"
                                            fullWidth
                                            type={field.type}
                                            label={field.label}
                                            required={field.required}
                                            value={formData[field.id] || ''}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={handleChange}
                                            error={Boolean(errorInputs[field.id]?.isError)}
                                            helperText={errorInputs[field.id]?.messageError}
                                        />
                                    )
                                }
                            </FormGroup>

                            <Box
                                sx={{
                                    width: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: '2px'
                                }}
                            >
                                {field.help && (
                                    <HelpIconPopover htmlContent={field.help} />
                                )}
                            </Box>
                        </Box>
                    ))
            }
        </form >
    );
};
export default DynamicForm;